import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import DescriptionIcon from "@mui/icons-material/Description";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningIcon from "@mui/icons-material/Warning";
import AddPaymentDescriptor from "../components/AddPaymentDescriptor";
import { useState, useEffect } from "react";
import RenderOnRole from "../components/RenderOnRole";
import { useChosenEmployer } from "../contexts/EmployerContext";

async function fetchAllEmployers() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/all?excludeRelations=true`
  );
  return response;
}

async function fetchSubDepartments(companyId) {
  if (!companyId) return { data: [] };
  const response = await HttpService.getAxiosClient().get(
    `${window.API_URL}/subdepartment/options?companyId=${companyId}`
  );
  return response;
}

// Status constants
const STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  NEEDS_REVIEW: "NEEDS_REVIEW",
  REJECTED: "REJECTED",
};

// Helper function to extract error message from backend response
function getErrorMessage(error, defaultMessage) {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data) {
    return typeof error.response.data === "string"
      ? error.response.data
      : defaultMessage;
  }
  if (error?.message) {
    return error.message;
  }
  return defaultMessage;
}

export default function PaymentDescriptorsConfig() {
  const { chosenEmployerId, employerData } = useChosenEmployer();
  const [rowStates, setRowStates] = useState({}); // Manages all row states in one object
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });
  const [addDescriptorDlg, setAddDescriptorDlg] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [selectedSubDepartment, setSelectedSubDepartment] = useState(null);
  const queryClient = useQueryClient();

  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["allEmployers"],
    fetchAllEmployers,
    { enabled: chosenEmployerId === "ANY" }
  );
  // Determine which company id to use for loading subdepartments:
  // - for super users (ANY) use the selected employer from the combo
  // - for normal users use their `employerData.idEmployer`
  const companyIdForSubDeps =
    chosenEmployerId === "ANY"
      ? selectedEmployer?.idEmployer
      : (employerData?.idEmployer || (chosenEmployerId && chosenEmployerId !== "ANY" ? chosenEmployerId : undefined));

  const { data: subDepartmentsData, isLoading: isLoadingSubDepartments } =
      useQuery(
        ["subDepartments", companyIdForSubDeps],
        () => fetchSubDepartments(companyIdForSubDeps),
        {
          enabled:
            !!companyIdForSubDeps &&
            (function () {
              const e =
                chosenEmployerId === "ANY"
                  ? selectedEmployer || (employersData?.data || []).find((x) => x.idEmployer === companyIdForSubDeps)
                  : employerData || (employersData?.data || []).find((x) => x.idEmployer === companyIdForSubDeps);
              if (!e) return true;
              return !(
                !!e.parentEmployerId ||
                !!e.parentId ||
                !!e.parent ||
                !!e.isSubdepartment ||
                !!e.isSubDepartment ||
                e.type === "SUBDEPARTMENT" ||
                e.type === "SUB_DEPARTMENT" ||
                e.employerType === "SUBDEPARTMENT" ||
                e.employerType === "SUB_DEPARTMENT"
              );
            })(),
        }
      );

  // Clear subdepartment selection when the company changes
  useEffect(() => {
    setSelectedSubDepartment(null);
  }, [companyIdForSubDeps]);
  // Resolve the company object for inspection (selected employer for ANY users, or employerData)
  const resolvedCompany =
    chosenEmployerId === "ANY"
      ? selectedEmployer || (employersData?.data || []).find((e) => e.idEmployer === companyIdForSubDeps)
      : employerData || (employersData?.data || []).find((e) => e.idEmployer === companyIdForSubDeps);

  function isSubdepartmentEmployer(e) {
    if (!e) return false;
    return (
      !!e.parentEmployerId ||
      !!e.parentId ||
      !!e.parent ||
      !!e.isSubdepartment ||
      !!e.isSubDepartment ||
      e.type === "SUBDEPARTMENT" ||
      e.type === "SUB_DEPARTMENT" ||
      e.employerType === "SUBDEPARTMENT" ||
      e.employerType === "SUB_DEPARTMENT"
    );
  }

  const showSubdepartmentSelector = !!companyIdForSubDeps && !isSubdepartmentEmployer(resolvedCompany);


  // Helper functions
  const updateRowState = (rowId, updates) => {
    setRowStates((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], ...updates },
    }));
  };

  const clearRowState = (rowId) => {
    setRowStates((prev) => {
      const newState = { ...prev };
      delete newState[rowId];
      return newState;
    });
  };

  const getRowState = (rowId) => rowStates[rowId] || {};

  const updatePaymentDescriptorMutation = useMutation(
    (values) => {
      const updateData = {
        description: values.row.description,
        descriptorId: values.row.descriptorId,
        status: values.row.status,
        avoidMaxYearlyValidation: values.row.avoidMaxYearlyValidation,
      };
      return HttpService.getAxiosClient().put(
        window.API_URL + `/payment-descriptors/${values.id}`,
        updateData
      );
    },
    {
      onMutate: (variables) => {
        updateRowState(variables.id, {
          loading: variables.actionType || "update",
        });
      },
      onSuccess: (data, variables) => {
        clearRowState(variables.id);
        queryClient.invalidateQueries("paymentDescriptors");
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "paymentDescriptorsConfig.UpdateSuccess",
            "Payment descriptor updated successfully"
          ),
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "paymentDescriptorsConfig.ErrorUpdating",
              "Error updating the payment descriptor"
            )
          ),
        });
      },
      onSettled: (data, error, variables) => {
        updateRowState(variables.id, { loading: null });
      },
    }
  );

  const deletePaymentDescriptorMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/payment-descriptors/${values.id}`
      );
    },
    {
      onMutate: (variables) => {
        // Set loading state for this specific row and action
        setRowStates((prev) => ({
          ...prev,
          [`${variables.id}_delete`]: true,
        }));
      },
      onSuccess: (dataResponse, value) => {
        queryClient.invalidateQueries("paymentDescriptors");
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "paymentDescriptorsConfig.DeleteSuccess",
            "Payment descriptor deleted successfully"
          ),
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "paymentDescriptorsConfig.ErrorDeleting",
              "Error deleting the payment descriptor"
            )
          ),
        });
      },
      onSettled: (data, error, variables) => {
        // Clear loading state for this specific row and action
        setRowStates((prev) => {
          const newState = { ...prev };
          delete newState[`${variables.id}_delete`];
          return newState;
        });
      },
    }
  );

  const effectiveEmployerId =
    chosenEmployerId === "ANY"
      ?
        // prefer subdepartment selection when present, otherwise selected company
        (selectedSubDepartment?.id || selectedSubDepartment?.idEmployer || selectedEmployer?.idEmployer)
      : employerData?.idEmployer || chosenEmployerId;

  const requiresSelection = chosenEmployerId === "ANY";
  const waitingForSelection =
    requiresSelection && !selectedEmployer?.idEmployer;

  const handleCellEditCommit = (params) => {
    const { id, field, value } = params;
    const currentRow = sortedData.find((row) => row.id === id);
    const oldValue = currentRow?.[field];

    updateRowState(id, {
      hasChanges: true,
      previousValues: { ...getRowState(id).previousValues, [field]: oldValue },
      editedValues: { ...getRowState(id).editedValues, [field]: value },
    });
  };

  const getStatusChip = (row) => {
    let color = "default";
    switch (row.status) {
      case STATUS.ACTIVE:
        color = "success";
        break;
      case STATUS.INACTIVE:
        color = "default";
        break;
      case STATUS.NEEDS_REVIEW:
        color = "warning";
        break;
      case STATUS.REJECTED:
        color = "error";
        break;
      default:
        color = "default";
        break;
    }
    const chip = <Chip label={row.status.replace('_', ' ')} color={color} size="small" />;
    
    if (row.status === STATUS.NEEDS_REVIEW && row.evaluationReason) {
      return (
        <Tooltip title={row.evaluationReason} arrow placement="top">
          {chip}
        </Tooltip>
      );
    }
    return chip;
  };

  const columns = [
    {
      field: "employerName",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.employerName",
        "Employer"
      ),
      width: 200,
      hide: false,
      renderCell: (params) => {
        const parentName = params.row.parentEmployerName;
        if (parentName && parentName !== params.value) {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                lineHeight: 1.1,
              }}
            >
              <Typography variant="caption" sx={{ lineHeight: 1.1 }}>
                {params.value}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.1, fontSize: "0.75rem" }}
              >
                {parentName}
              </Typography>
            </Box>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              lineHeight: 1.1,
            }}
          >
            <Typography variant="caption" sx={{ lineHeight: 1.1 }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "descriptorId",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.descriptorId",
        "Descriptor ID"
      ),
      width: 130,
      editable: true,
      type: "number",
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.description",
        "Description"
      ),
      width: 400,
      editable: true,
    },

    {
      field: "paymentInfo",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.paymentInfo",
        "Payment Info"
      ),
      width: 200,
      editable: false,
      renderCell: (params) => {
        const amountType = params.row.amountType;
        const paymentAmount = params.row.paymentAmount;
        const avoidMaxYearlyValidation = params.row.avoidMaxYearlyValidation;

        const warningIcon = avoidMaxYearlyValidation ? (
          <Tooltip title={Labels.getLabel("paymentDescriptorsConfig.avoidYearlyValidation", "Avoids Max Yearly Validation")} arrow placement="top">
            <WarningIcon color="warning" fontSize="small" />
          </Tooltip>
        ) : null;

        if (!amountType) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {warningIcon}
              <Chip
                label="UNRESTRICTED"
                color="primary"
                size="small"
                variant="outlined"
                sx={{ fontWeight: "bold", fontSize: "0.7rem" }}
              />
            </Box>
          );
        }

        const formattedAmount =
          paymentAmount != null
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(paymentAmount)
            : "";

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {warningIcon}
            <Chip
              label={amountType}
              color={amountType === "FIXED_AMOUNT" ? "primary" : "default"}
              size="small"
              sx={{ fontWeight: "bold", fontSize: "0.7rem" }}
            />
            {formattedAmount && (
              <Typography
                variant="body2"
                sx={{ fontSize: "0.85rem", fontWeight: 600 }}
              >
                {formattedAmount}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.status",
        "Status"
      ),
      width: 120,
      renderCell: (params) => getStatusChip(params.row),
    },
    {
      field: "createdBy",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.createdBy",
        "Created By"
      ),
      width: 120,
      hide: true,
    },
    {
      field: "createdDate",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.createdDate",
        "Created Date"
      ),
      width: 150,
      hide: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return "";
      },
    },
    {
      field: "updatedBy",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.updatedBy",
        "Updated By"
      ),
      width: 120,
      hide: true,
    },
    {
      field: "updatedDate",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.updatedDate",
        "Updated Date"
      ),
      width: 150,
      hide: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return "";
      },
    },
    {
      field: "action",
      headerName: Labels.getLabel(
        "paymentDescriptorsConfig.column.action",
        "Action"
      ),
      width: 200,
      renderCell: (params) => {
        const rowState = getRowState(params.id);
        const isLoading = (action) => rowState.loading === action;
        const hasChanges = rowState.hasChanges;
        const isDisabled = !!rowState.loading;
        const isEditable =
          params.row.status !== STATUS.INACTIVE &&
          params.row.status !== STATUS.REJECTED;

        const LoadingIcon = ({ action, children }) =>
          isLoading(action) ? <CircularProgress size={24} /> : children;

        return (
          <div style={{ display: "flex" }}>
            {/* Save Button */}
            {isEditable && (
              <RenderOnRole roles={["pd-modify"]}>
                <IconButton
                  disabled={!hasChanges || isDisabled}
                  onClick={() =>
                    updatePaymentDescriptorMutation.mutate({
                      ...params,
                      actionType: "save",
                    })
                  }
                  title={Labels.getLabel(
                    "paymentDescriptorsConfig.SaveChanges",
                    "Save Changes"
                  )}
                >
                  <LoadingIcon action="save">
                    <SaveIcon />
                  </LoadingIcon>
                </IconButton>
              </RenderOnRole>
            )}

            {/* Cancel Button */}
            {hasChanges && isEditable && (
              <RenderOnRole roles={["pd-modify"]}>
                <IconButton
                  onClick={() => clearRowState(params.id)}
                  title={Labels.getLabel(
                    "paymentDescriptorsConfig.CancelChanges",
                    "Cancel Changes"
                  )}
                  disabled={isDisabled}
                >
                  <CloseIcon />
                </IconButton>
              </RenderOnRole>
            )}

            {/* Action buttons when not editing */}
            {!hasChanges && (
              <>
                {/* Approve/Reject for NEEDS_REVIEW */}
                {params.row.status === STATUS.NEEDS_REVIEW && (
                  <RenderOnRole roles={["pd-approve"]}>
                    <IconButton
                      onClick={() => {
                        params.row.status = STATUS.ACTIVE;
                        updatePaymentDescriptorMutation.mutate({
                          ...params,
                          actionType: "approve",
                        });
                      }}
                      color="success"
                      title={Labels.getLabel(
                        "paymentDescriptorsConfig.Approve",
                        "Approve"
                      )}
                      disabled={isDisabled}
                    >
                      <LoadingIcon action="approve">
                        <CheckIcon />
                      </LoadingIcon>
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        params.row.status = STATUS.REJECTED;
                        updatePaymentDescriptorMutation.mutate({
                          ...params,
                          actionType: "reject",
                        });
                      }}
                      color="error"
                      title={Labels.getLabel(
                        "paymentDescriptorsConfig.Reject",
                        "Reject"
                      )}
                      disabled={isDisabled}
                    >
                      <LoadingIcon action="reject">
                        <CancelIcon />
                      </LoadingIcon>
                    </IconButton>
                  </RenderOnRole>
                )}

                {/* Toggle for ACTIVE/INACTIVE */}
                {(params.row.status === STATUS.ACTIVE ||
                  params.row.status === STATUS.INACTIVE) && (
                  <RenderOnRole roles={["pd-modify"]}>
                    <IconButton
                      onClick={() => {
                        const newStatus =
                          params.row.status === STATUS.ACTIVE
                            ? STATUS.INACTIVE
                            : STATUS.ACTIVE;
                        params.row.status = newStatus;
                        updatePaymentDescriptorMutation.mutate({
                          ...params,
                          actionType: "toggle",
                        });
                      }}
                      title={
                        params.row.status === STATUS.ACTIVE
                          ? Labels.getLabel(
                              "paymentDescriptorsConfig.Deactivate",
                              "Deactivate"
                            )
                          : Labels.getLabel(
                              "paymentDescriptorsConfig.Activate",
                              "Activate"
                            )
                      }
                      disabled={isDisabled}
                    >
                      <LoadingIcon action="toggle">
                        {params.row.status === STATUS.ACTIVE ? (
                          <ToggleOnIcon color="success" />
                        ) : (
                          <ToggleOffOutlinedIcon />
                        )}
                      </LoadingIcon>
                    </IconButton>
                  </RenderOnRole>
                )}

                {/* Delete Button */}
                <RenderOnRole roles={["pd-delete"]}>
                  <IconButton
                    onClick={() =>
                      deletePaymentDescriptorMutation.mutate({ ...params })
                    }
                    title={Labels.getLabel(
                      "paymentDescriptorsConfig.Delete",
                      "Delete"
                    )}
                    disabled={isDisabled}
                  >
                    <LoadingIcon action="delete">
                      <DeleteIcon />
                    </LoadingIcon>
                  </IconButton>
                </RenderOnRole>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const { data, isLoading, error, isFetching } = useQuery(
    ["paymentDescriptors", effectiveEmployerId, selectedSubDepartment?.id],
    async () => {
      let url = window.API_URL + `/payment-descriptors`;
      // For super users (ANY scope), send selected employer/subdepartment as query param if chosen
      if ((selectedSubDepartment?.id || effectiveEmployerId)) {
        // if a subdepartment is selected prefer its id, otherwise use the employer/company id
        const idToSend = selectedSubDepartment?.id || effectiveEmployerId;
        url += `?employerId=${idToSend}`;
      }
      
      const response = await HttpService.getAxiosClient().get(url);
      return response;
    },
    { keepPreviousData: true, staleTime: 3000, refetchOnWindowFocus: false,
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "paymentDescriptorsConfig.ErrorLoading",
              "Error loading payment descriptors"
            )
          ),
        });
      },
    }
  );

  const sortedData = data?.data
    ? [...data.data].sort((a, b) => b.id - a.id)
    : [];

  // Merge original data with edited values
  const mergedData = sortedData.map((row) => ({
    ...row,
    ...(getRowState(row.id).editedValues || {}),
  }));

  const addDescriptorDialog = (
    <Dialog open={addDescriptorDlg} fullWidth onClose={() => {}}>
      <Box
        sx={{
          backgroundColor: "primary.main",
          px: 3,
          minHeight: 52,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon sx={{ color: "white" }} />
          <Typography variant="h6" sx={{ color: "white" }}>
            {Labels.getLabel(
              "paymentDescriptorsConfig.AddPaymentDescriptor",
              "Add Payment Descriptor"
            )}
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            setAddDescriptorDlg(false);
          }}
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ paddingTop: 3 }}>
        <AddPaymentDescriptor handleClose={() => setAddDescriptorDlg(false)} />
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading && !waitingForSelection ? (
        <Typography>
          {Labels.getLabel(
            "paymentDescriptorsConfig.Loading",
            "Loading payment descriptors..."
          )}
        </Typography>
      ) : error ? (
        <Alert severity="error">
          {getErrorMessage(
            error,
            Labels.getLabel(
              "paymentDescriptorsConfig.ErrorLoading",
              "Error loading payment descriptors"
            )
          )}
        </Alert>
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2, gap: 2, flexWrap: "wrap" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5">
                  {Labels.getLabel(
                    "paymentDescriptorsConfig.PaymentDescriptors",
                    "Payment Descriptors"
                  )}
                </Typography>
                <IconButton 
                  onClick={() => queryClient.invalidateQueries("paymentDescriptors")}
                  title={Labels.getLabel("paymentDescriptorsConfig.Refresh", "Refresh")}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {chosenEmployerId === "ANY" && (
                  <Autocomplete
                    sx={{ minWidth: 400 }}
                    options={(employersData?.data || []).filter((o) => o.idEmployer)}
                    getOptionLabel={(option) =>
                      `${option.description}-[${option.idEmployer}]`
                    }
                    value={selectedEmployer}
                    onChange={(event, newValue) => {
                      setSelectedEmployer(newValue);
                      setSelectedSubDepartment(null);
                    }}
                    loading={isLoadingEmployers}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={Labels.getLabel(
                          "paymentDescriptorsConfig.filter.company",
                          "Company"
                        )}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.idEmployer === value?.idEmployer
                    }
                  />
                )}

                  { showSubdepartmentSelector && (
                    <>
                      <Autocomplete
                        sx={{ minWidth: 400 }}
                        options={subDepartmentsData?.data || []}
                        getOptionLabel={(option) =>
                          `${option.description}-[${option.id}]`
                        }
                        value={selectedSubDepartment}
                        onChange={(event, newValue) =>
                          setSelectedSubDepartment(newValue)
                        }
                        loading={isLoadingSubDepartments}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={Labels.getLabel(
                              "paymentDescriptorsConfig.filter.subdepartment",
                              "Subdepartment"
                            )}
                          />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value?.id
                        }
                      />
                      {isLoadingSubDepartments && (
                        <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                          <CircularProgress size={18} />
                        </Box>
                      )}
                    </>
                  )}

                <RenderOnRole roles={["pd-create"]}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setAddDescriptorDlg(true)}
                  >
                    {Labels.getLabel(
                      "paymentDescriptorsConfig.AddPaymentDescriptor",
                      "Add Payment Descriptor"
                    )}
                  </Button>
                </RenderOnRole>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
                // remove dynamic key to avoid unmount/remount when rows change
                rows={mergedData}
                columns={columns}
                getRowHeight={() => 35}
                density="compact"
                isCellEditable={(params) =>
                  params.row.status !== STATUS.INACTIVE &&
                  params.row.status !== STATUS.REJECTED
                }
                onCellEditCommit={handleCellEditCommit}
                disableDensitySelector
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
          </Grid>
        </Grid>
      )}
      {addDescriptorDlg && addDescriptorDialog}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}